import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { PROPERTIES, COUNTIES } from "@/data";
import { PropertyCard } from "@/components/PropertyCard";
import { Reveal } from "@/components/Motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Search() {
  const [sp] = useSearchParams();
  const [f, setF] = useState({
    county: sp.get("county") || "",
    type: sp.get("type") || "",
    minPrice: sp.get("minPrice") || "",
    maxPrice: sp.get("maxPrice") || "",
    beds: "",
    newConstruction: sp.get("newConstruction") === "true",
    basement: sp.get("basement") === "true",
    noHoa: sp.get("noHoa") === "true",
    acreage: sp.get("acreage") === "true",
  });
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const results = useMemo(() => PROPERTIES.filter((p) => {
    if (f.county && p.county !== f.county) return false;
    if (f.type && p.type !== f.type) return false;
    if (f.minPrice && p.price < +f.minPrice) return false;
    if (f.maxPrice && p.price > +f.maxPrice) return false;
    if (f.beds && p.beds < +f.beds) return false;
    if (f.newConstruction && !p.newConstruction) return false;
    if (f.basement && !p.basement) return false;
    if (f.noHoa && p.hoa) return false;
    if (f.acreage && !p.lot.includes("ac")) return false;
    return true;
  }), [f]);

  const sel = (k, children) => (
    <select value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`filter-${k}`} className="w-full border border-input rounded-md h-10 px-3 bg-white text-sm">{children}</select>
  );
  const chk = (k, label) => (
    <label className="flex items-center gap-2 text-sm text-charcoal/80 cursor-pointer">
      <input type="checkbox" checked={f[k]} onChange={(e) => set(k, e.target.checked)} data-testid={`filter-${k}`} className="accent-gold w-4 h-4" /> {label}
    </label>
  );

  return (
    <div className="bg-cream pt-24 min-h-screen">
      <section className="px-6 md:px-12 py-10 max-w-7xl mx-auto">
        <Reveal><h1 className="font-serif text-4xl md:text-5xl text-navy tracking-tighter mb-1">Property search</h1></Reveal>
        <p className="text-charcoal/50 text-sm mb-8">Sample placeholder listings for demonstration — not live MLS/IDX data.</p>
        <div className="grid lg:grid-cols-4 gap-8">
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-3xl p-6 border border-black/5 sticky top-28 space-y-4">
              <div className="flex items-center gap-2 font-medium text-navy"><SlidersHorizontal size={18} /> Filters</div>
              <div><Label>County</Label>{sel("county", <><option value="">All counties</option>{COUNTIES.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}</>)}</div>
              <div><Label>Property type</Label>{sel("type", <><option value="">All types</option>{["Single Family", "Townhome", "Condo"].map((t) => <option key={t}>{t}</option>)}</>)}</div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label>Min $</Label><Input type="number" value={f.minPrice} onChange={(e) => set("minPrice", e.target.value)} data-testid="filter-minPrice" /></div>
                <div><Label>Max $</Label><Input type="number" value={f.maxPrice} onChange={(e) => set("maxPrice", e.target.value)} data-testid="filter-maxPrice" /></div>
              </div>
              <div><Label>Min beds</Label>{sel("beds", <><option value="">Any</option>{[1, 2, 3, 4, 5].map((b) => <option key={b} value={b}>{b}+</option>)}</>)}</div>
              <div className="space-y-2 pt-2">
                {chk("newConstruction", "New construction")}
                {chk("basement", "Finished basement")}
                {chk("noHoa", "No HOA")}
                {chk("acreage", "Acreage")}
              </div>
            </div>
          </aside>
          <div className="lg:col-span-3">
            <p className="text-charcoal/60 text-sm mb-5" data-testid="results-count">{results.length} home{results.length !== 1 ? "s" : ""} found</p>
            {results.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-black/5" data-testid="empty-state">
                <p className="text-charcoal/60">No sample homes match these filters. Try widening your search.</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {results.map((p) => <PropertyCard key={p.id} p={p} />)}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
